import React from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import MuxPlayer from "@mux/mux-player-react";
import { apiUrl, getApiBase } from "../lib/apiBase";
import { getAuthToken } from "../lib/auth";

function getCachedUser() {
  for (const key of ["user", "ga_user", "member_user"]) {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object") return parsed;
    } catch {}
  }
  return null;
}

function mediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${getApiBase()}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

function isPhoto(post) {
  return String(post?.mediaType || "").toLowerCase() === "image" || Boolean(post?.imageUrl);
}

function isText(post) {
  return String(post?.mediaType || "").toLowerCase() === "text";
}

function isClip(post) {
  return Boolean(post?.isClip || post?.clip);
}

function typeLabel(post) {
  if (isPhoto(post)) return "PHOTO";
  if (isText(post)) return "POST";
  if (isClip(post)) return "CLIP";
  return "VIDEO";
}

function createdLabel(post) {
  const value = Number(post?.createdAt || 0);
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "";
  }
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function ExpandedVideo({ post }) {
  const playerRef = React.useRef(null);
  const clip = isClip(post);
  const start = Math.max(0, Number(post?.clipStartOffsetSeconds || 0));
  const rawEnd = Number(post?.clipEndOffsetSeconds || 0);
  const configuredDuration = Math.max(1, Number(post?.durationSeconds || 30));
  const end = clip
    ? rawEnd > start
      ? rawEnd
      : start + configuredDuration
    : Number.POSITIVE_INFINITY;

  React.useEffect(() => {
    const player = playerRef.current;
    if (!player || !clip) return undefined;

    const placeAtStart = () => {
      try {
        const current = Number(player.currentTime || 0);
        if (current < start || current >= end) player.currentTime = start;
      } catch {}
    };
    const keepInsideClip = () => {
      const current = Number(player.currentTime || 0);
      if (current >= end) {
        try {
          player.pause();
          player.currentTime = start;
        } catch {}
      }
    };

    player.addEventListener("loadedmetadata", placeAtStart);
    player.addEventListener("timeupdate", keepInsideClip);
    return () => {
      player.removeEventListener("loadedmetadata", placeAtStart);
      player.removeEventListener("timeupdate", keepInsideClip);
    };
  }, [clip, end, start]);

  return (
    <MuxPlayer
      ref={playerRef}
      streamType={post?.streamType === "live" ? "live" : "on-demand"}
      playbackId={String(post?.playbackId || "").trim()}
      startTime={clip ? start : undefined}
      controls
      autoPlay={false}
      metadata={{
        video_id: String(post?.id || ""),
        video_title: post?.caption || (clip ? "Stream clip" : "Feed video"),
        viewer_user_id: "profile",
      }}
      style={{ width: "100%", display: "block", aspectRatio: "16 / 9" }}
    />
  );
}

function PostPreview({ post, onOpen }) {
  if (isText(post)) {
    return (
      <button type="button" onClick={onOpen} style={styles.textPreview}>
        {post.caption || "Post"}
      </button>
    );
  }

  if (isPhoto(post)) {
    return (
      <button type="button" onClick={onOpen} style={styles.mediaButton}>
        <img
          src={mediaUrl(post.imageUrl)}
          alt={post.caption || "GAStreaming photo"}
          loading="lazy"
          style={styles.photo}
        />
        <span style={styles.expandPill}>⛶ View larger</span>
      </button>
    );
  }

  if (post?.playbackId) {
    return (
      <div style={styles.videoPreview}>
        <ExpandedVideo post={post} />
        <button type="button" onClick={onOpen} style={styles.videoExpandButton}>
          ⛶ View larger
        </button>
      </div>
    );
  }

  return (
    <button type="button" onClick={onOpen} style={styles.waitingMedia}>
      This clip is still processing.
    </button>
  );
}

function ProfilePostCard({ post, onOpen, onComments, onLike, onShare, onDelete, deleting }) {
  return (
    <article style={styles.postCard}>
      <header style={styles.postHeader}>
        <div style={{ minWidth: 0 }}>
          <div style={styles.typeRow}>
            <span style={styles.typePill}>{typeLabel(post)}</span>
            <strong style={styles.postTitle}>{post.caption || "GAStreaming post"}</strong>
          </div>
          <small style={styles.metaText}>{createdLabel(post)}</small>
        </div>
      </header>

      <PostPreview post={post} onOpen={() => onOpen(post)} />

      <div style={styles.actionGrid}>
        <button
          type="button"
          onClick={() => onLike(post)}
          style={post?.likedByMe ? styles.actionLiked : styles.actionButton}
        >
          {post?.likedByMe ? "♥" : "♡"} Like {Number(post?.likeCount || 0)}
        </button>
        <button type="button" onClick={() => onComments(post)} style={styles.actionButton}>
          💬 Comments {Number(post?.commentCount || 0)}
        </button>
        <button type="button" onClick={() => onShare(post)} style={styles.actionButton}>
          ↗ Share
        </button>
        <button
          type="button"
          onClick={() => onDelete(post)}
          disabled={deleting === String(post?.id)}
          style={styles.deleteButton}
        >
          {deleting === String(post?.id) ? "Deleting…" : "Delete"}
        </button>
      </div>
    </article>
  );
}

export default function NativeProfilePostEnhancer() {
  const location = useLocation();
  const [posts, setPosts] = React.useState([]);
  const [host, setHost] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [panel, setPanel] = React.useState("media");
  const [uploadsOpen, setUploadsOpen] = React.useState(false);
  const [comments, setComments] = React.useState([]);
  const [commentsLoading, setCommentsLoading] = React.useState(false);
  const [commentText, setCommentText] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [deleting, setDeleting] = React.useState("");

  const active = String(location.pathname || "") === "/me";

  const loadPosts = React.useCallback(async () => {
    if (!active) return;
    const user = getCachedUser();
    const userId = String(user?.id || "").trim();
    if (!userId) return;

    try {
      const token = getAuthToken();
      const response = await fetch(apiUrl(`/posts/user/${encodeURIComponent(userId)}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
    } catch {}
  }, [active]);

  React.useEffect(() => {
    loadPosts();
    const refresh = () => loadPosts();
    window.addEventListener("ga:photo-post-created", refresh);
    window.addEventListener("ga:feed-post-created", refresh);
    window.addEventListener("auth:changed", refresh);
    return () => {
      window.removeEventListener("ga:photo-post-created", refresh);
      window.removeEventListener("ga:feed-post-created", refresh);
      window.removeEventListener("auth:changed", refresh);
    };
  }, [loadPosts]);

  React.useEffect(() => {
    if (!active) {
      setHost(null);
      return undefined;
    }

    const boundButtons = new Map();
    let openedVideoFromQuery = false;

    const openComposer = (mode = "text") => {
      window.dispatchEvent(new CustomEvent("ga:open-post-composer", { detail: { mode } }));
    };

    const hideLegacyProfileSections = () => {
      const notificationSection = document.getElementById("notifications");
      if (notificationSection) notificationSection.style.display = "none";

      const uploadHeading = Array.from(document.querySelectorAll("#root div")).find(
        (element) =>
          element.children.length === 0 && String(element.textContent || "").trim() === "Uploads"
      );
      if (uploadHeading instanceof HTMLElement) {
        uploadHeading.style.display = "none";
        let sibling = uploadHeading.nextElementSibling;
        while (sibling) {
          sibling.style.display = "none";
          sibling = sibling.nextElementSibling;
        }
      }
    };

    const bindUploadButton = () => {
      const button = Array.from(document.querySelectorAll("#root button")).find((candidate) => {
        const text = String(candidate.textContent || "").trim().toLowerCase();
        return text === "upload gameplay" || text === "upload to feed";
      });
      if (!(button instanceof HTMLButtonElement)) return;

      button.textContent = "Upload to Feed";
      if (!boundButtons.has(button)) {
        const handler = (event) => {
          if (button.dataset.gaAllowVideoUpload === "1") {
            delete button.dataset.gaAllowVideoUpload;
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          openComposer("text");
        };
        button.addEventListener("click", handler, true);
        boundButtons.set(button, handler);
      }

      if (!openedVideoFromQuery) {
        const params = new URLSearchParams(window.location.search);
        if (params.get("upload") === "video") {
          openedVideoFromQuery = true;
          button.dataset.gaAllowVideoUpload = "1";
          button.click();
          params.delete("upload");
          const query = params.toString();
          window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
        }
      }
    };

    const ensureHost = () => {
      let node = document.getElementById("ga-profile-social-hub-root");
      if (!node) {
        const achievements = document.getElementById("tournament-achievements");
        if (achievements?.parentElement) {
          node = document.createElement("div");
          node.id = "ga-profile-social-hub-root";
          achievements.insertAdjacentElement("afterend", node);
        }
      }
      if (node && node !== host) setHost(node);
    };

    const sync = () => {
      hideLegacyProfileSections();
      bindUploadButton();
      ensureHost();
    };

    sync();
    const timer = window.setInterval(sync, 600);
    return () => {
      window.clearInterval(timer);
      for (const [button, handler] of boundButtons.entries()) {
        button.removeEventListener("click", handler, true);
      }
    };
  }, [active, host]);

  const sharePost = React.useCallback(async (post) => {
    const url = `${window.location.origin}/feed#post-${encodeURIComponent(post.id)}`;
    const title = String(post?.caption || "GAStreaming post");
    const text = `Check out ${title} on GAStreaming.live.`;
    setMessage("");
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
      await copyText(url);
      setMessage("Post link copied.");
    } catch (error) {
      if (error?.name !== "AbortError") setMessage("Could not share this post.");
    }
  }, []);

  const openMedia = React.useCallback((post) => {
    setSelected(post);
    setPanel("media");
    setMessage("");
  }, []);

  const loadComments = React.useCallback(async (post) => {
    if (!post?.id) return;
    setCommentsLoading(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl(`/posts/${encodeURIComponent(post.id)}/comments`));
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || "Could not load comments.");
      setComments(Array.isArray(data?.comments) ? data.comments : []);
    } catch (error) {
      setMessage(error?.message || "Could not load comments.");
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const openComments = React.useCallback(
    (post) => {
      setSelected(post);
      setPanel("comments");
      setCommentText("");
      loadComments(post);
    },
    [loadComments]
  );

  const toggleLike = React.useCallback(async (post) => {
    const token = getAuthToken();
    if (!token || !post?.id) return;
    setMessage("");
    try {
      const response = await fetch(apiUrl(`/posts/${encodeURIComponent(post.id)}/like`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || "Could not update like.");
      const patch = {
        likeCount: Number(data?.likeCount || 0),
        likedByMe: Boolean(data?.likedByMe),
        lastLikedBy: data?.lastLikedBy || null,
      };
      setPosts((current) =>
        current.map((item) => (String(item.id) === String(post.id) ? { ...item, ...patch } : item))
      );
      setSelected((current) =>
        current && String(current.id) === String(post.id) ? { ...current, ...patch } : current
      );
    } catch (error) {
      setMessage(error?.message || "Could not update like.");
    }
  }, []);

  const deletePost = React.useCallback(
    async (post) => {
      const token = getAuthToken();
      if (!token || !post?.id || deleting) return;
      const confirmed = window.confirm("Delete this post from your profile and the Feed?");
      if (!confirmed) return;

      setDeleting(String(post.id));
      setMessage("");
      try {
        const response = await fetch(apiUrl(`/posts/${encodeURIComponent(post.id)}`), {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.ok) {
          throw new Error(data?.message || data?.error || "Could not delete this post.");
        }
        setSelected((current) => (String(current?.id || "") === String(post.id) ? null : current));
        await loadPosts();
      } catch (error) {
        setMessage(error?.message || "Could not delete this post.");
      } finally {
        setDeleting("");
      }
    },
    [deleting, loadPosts]
  );

  React.useEffect(() => {
    if (!selected && !uploadsOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [selected, uploadsOpen]);

  const addComment = async () => {
    const text = String(commentText || "").trim();
    const token = getAuthToken();
    if (!selected?.id || !text || !token || busy) return;

    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl(`/posts/${encodeURIComponent(selected.id)}/comments`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || "Could not post comment.");
      setCommentText("");
      await loadComments(selected);
      setPosts((current) =>
        current.map((post) =>
          String(post.id) === String(selected.id)
            ? { ...post, commentCount: Number(post.commentCount || 0) + 1 }
            : post
        )
      );
      setSelected((current) =>
        current ? { ...current, commentCount: Number(current.commentCount || 0) + 1 } : current
      );
    } catch (error) {
      setMessage(error?.message || "Could not post comment.");
    } finally {
      setBusy(false);
    }
  };

  if (!active) return null;

  const user = getCachedUser() || {};
  const displayName = String(user?.displayName || user?.username || user?.name || "Member");
  const avatar = mediaUrl(user?.avatarUrl);
  const uploads = posts.filter((post) => !isText(post));
  const recentPosts = posts.slice(0, 6);

  const socialHub = host
    ? createPortal(
        <section style={styles.hub} aria-label="Profile posts">
          <div style={styles.hubHeader}>
            <div>
              <div style={styles.eyebrow}>YOUR GA PROFILE</div>
              <h2 style={styles.hubTitle}>Posts</h2>
              <p style={styles.hubCopy}>
                Share updates, photos, and videos here. Everything also appears on the community Feed.
              </p>
            </div>
            <button type="button" style={styles.uploadsButton} onClick={() => setUploadsOpen(true)}>
              Uploads {uploads.length}
            </button>
          </div>

          <button
            type="button"
            style={styles.composerBar}
            onClick={() => window.dispatchEvent(new CustomEvent("ga:open-post-composer", { detail: { mode: "text" } }))}
          >
            <span style={styles.avatarSmall}>
              {avatar ? <img src={avatar} alt="" style={styles.avatarImage} /> : displayName.charAt(0)}
            </span>
            <span style={styles.composerPrompt}>What’s on your mind, {displayName}?</span>
          </button>

          <div style={styles.quickActions}>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("ga:open-post-composer", { detail: { mode: "text" } }))}
              style={styles.quickButton}
            >
              ✍️ Post
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("ga:open-post-composer", { detail: { mode: "photo" } }))}
              style={styles.quickButton}
            >
              📷 Photo
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("ga:open-post-composer", { detail: { mode: "video" } }))}
              style={styles.quickButton}
            >
              🎥 Video
            </button>
          </div>

          {message ? <div style={styles.message}>{message}</div> : null}

          <div style={styles.timelineHeader}>
            <strong>Recent posts</strong>
            <button type="button" onClick={loadPosts} style={styles.refreshButton}>Refresh</button>
          </div>

          {recentPosts.length ? (
            <div style={styles.timeline}>
              {recentPosts.map((post) => (
                <ProfilePostCard
                  key={post.id}
                  post={post}
                  onOpen={openMedia}
                  onComments={openComments}
                  onLike={toggleLike}
                  onShare={sharePost}
                  onDelete={deletePost}
                  deleting={deleting}
                />
              ))}
            </div>
          ) : (
            <div style={styles.empty}>No posts yet. Create your first post above.</div>
          )}
        </section>,
        host
      )
    : null;

  const postModal = selected && typeof document !== "undefined"
    ? createPortal(
        <div
          className="ga-profile-post-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelected(null);
          }}
        >
          <section role="dialog" aria-modal="true" aria-label="Profile post">
            <header>
              <div>
                <small>{typeLabel(selected)}</small>
                <h2>{selected.caption || "GAStreaming post"}</h2>
              </div>
              <button type="button" onClick={() => setSelected(null)} aria-label="Close post">×</button>
            </header>

            <div className="ga-profile-post-tabs">
              <button type="button" className={panel === "media" ? "is-active" : ""} onClick={() => setPanel("media") }>
                View post
              </button>
              <button
                type="button"
                className={panel === "comments" ? "is-active" : ""}
                onClick={() => {
                  setPanel("comments");
                  loadComments(selected);
                }}
              >
                Comments {Number(selected.commentCount || 0)}
              </button>
            </div>

            <div className="ga-profile-post-modal-content">
              {panel === "media" ? (
                <>
                  <div className="ga-profile-expanded-media">
                    {isText(selected) ? (
                      <div style={styles.expandedText}>{selected.caption}</div>
                    ) : isPhoto(selected) ? (
                      <img src={mediaUrl(selected.imageUrl)} alt={selected.caption || "GAStreaming photo"} />
                    ) : selected.playbackId ? (
                      <ExpandedVideo post={selected} />
                    ) : (
                      <div className="ga-profile-media-waiting">This clip is still processing.</div>
                    )}
                  </div>
                  <div className="ga-profile-expanded-actions">
                    <button type="button" className={selected.likedByMe ? "is-liked" : ""} onClick={() => toggleLike(selected)}>
                      {selected.likedByMe ? "♥" : "♡"} Like {Number(selected.likeCount || 0)}
                    </button>
                    <button type="button" onClick={() => { setPanel("comments"); loadComments(selected); }}>
                      💬 Comments {Number(selected.commentCount || 0)}
                    </button>
                    <button type="button" onClick={() => sharePost(selected)}>↗ Share</button>
                    <button type="button" onClick={() => deletePost(selected)}>Delete</button>
                  </div>
                </>
              ) : (
                <div className="ga-profile-comments-panel">
                  <div className="ga-profile-comment-composer">
                    <textarea
                      value={commentText}
                      onChange={(event) => setCommentText(event.target.value.slice(0, 500))}
                      placeholder="Write a comment…"
                      rows={3}
                      disabled={busy}
                    />
                    <button type="button" onClick={addComment} disabled={!String(commentText).trim() || busy}>
                      {busy ? "Posting…" : "Post comment"}
                    </button>
                  </div>

                  {commentsLoading ? (
                    <div style={styles.empty}>Loading comments…</div>
                  ) : comments.length ? (
                    <div style={styles.commentsList}>
                      {comments.map((comment) => (
                        <div key={comment.id} style={styles.commentCard}>
                          <strong>@{comment.username || comment.displayName || "member"}</strong>
                          <div style={{ marginTop: 5, whiteSpace: "pre-wrap" }}>{comment.text}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.empty}>No comments yet.</div>
                  )}
                </div>
              )}

              {message ? <div className="ga-profile-post-message">{message}</div> : null}
            </div>
          </section>
        </div>,
        document.body
      )
    : null;

  const uploadsModal = uploadsOpen && typeof document !== "undefined"
    ? createPortal(
        <div
          className="ga-profile-post-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setUploadsOpen(false);
          }}
        >
          <section role="dialog" aria-modal="true" aria-label="Your uploads">
            <header>
              <div>
                <small>PROFILE MEDIA</small>
                <h2>Uploads</h2>
              </div>
              <button type="button" onClick={() => setUploadsOpen(false)} aria-label="Close uploads">×</button>
            </header>
            <div style={styles.uploadModalBody}>
              {uploads.length ? (
                uploads.map((post) => (
                  <ProfilePostCard
                    key={post.id}
                    post={post}
                    onOpen={(item) => {
                      setUploadsOpen(false);
                      openMedia(item);
                    }}
                    onComments={(item) => {
                      setUploadsOpen(false);
                      openComments(item);
                    }}
                    onLike={toggleLike}
                    onShare={sharePost}
                    onDelete={deletePost}
                    deleting={deleting}
                  />
                ))
              ) : (
                <div style={styles.empty}>No photo or video uploads yet.</div>
              )}
            </div>
          </section>
        </div>,
        document.body
      )
    : null;

  return <>{socialHub}{postModal}{uploadsModal}</>;
}

const baseButton = {
  minHeight: 42,
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.13)",
  background: "rgba(255,255,255,.055)",
  color: "white",
  fontWeight: 900,
  cursor: "pointer",
};

const styles = {
  hub: {
    marginTop: 18,
    padding: 18,
    borderRadius: 20,
    border: "1px solid rgba(255,255,255,.12)",
    background: "radial-gradient(800px 350px at 10% 0%, rgba(255,0,64,.15), transparent 65%), rgba(8,10,16,.76)",
    color: "white",
    boxShadow: "0 18px 55px rgba(0,0,0,.35)",
  },
  hubHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  eyebrow: { fontSize: 11, fontWeight: 1000, color: "#ff8da2", letterSpacing: 1.2 },
  hubTitle: { margin: "4px 0 2px", fontSize: "clamp(26px, 5vw, 38px)" },
  hubCopy: { margin: 0, opacity: .72, lineHeight: 1.45, maxWidth: 720 },
  uploadsButton: {
    ...baseButton,
    borderRadius: 999,
    padding: "0 16px",
    background: "rgba(255,255,255,.08)",
  },
  composerBar: {
    width: "100%",
    marginTop: 16,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,.13)",
    background: "rgba(0,0,0,.34)",
    color: "white",
    cursor: "pointer",
    textAlign: "left",
  },
  avatarSmall: {
    width: 44,
    height: 44,
    flex: "0 0 44px",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    background: "rgba(255,0,64,.24)",
    fontWeight: 1000,
  },
  avatarImage: { width: "100%", height: "100%", objectFit: "cover" },
  composerPrompt: { opacity: .72, fontWeight: 800, fontSize: 15 },
  quickActions: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },
  quickButton: { ...baseButton },
  timelineHeader: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid rgba(255,255,255,.1)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  refreshButton: { ...baseButton, minHeight: 36, padding: "0 12px" },
  timeline: { marginTop: 12, display: "grid", gap: 14 },
  postCard: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,.11)",
    background: "linear-gradient(145deg, rgba(38,8,15,.82), rgba(5,7,10,.88))",
    display: "grid",
    gap: 11,
  },
  postHeader: { display: "flex", justifyContent: "space-between", gap: 12 },
  typeRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  typePill: {
    flex: "0 0 auto",
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(255,210,110,.3)",
    background: "rgba(160,85,0,.18)",
    color: "#ffdf8a",
    fontSize: 10,
    fontWeight: 1000,
    letterSpacing: .7,
  },
  postTitle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  metaText: { opacity: .58, fontSize: 11 },
  textPreview: {
    width: "100%",
    padding: "20px 16px",
    borderRadius: 15,
    border: "1px solid rgba(255,255,255,.1)",
    background: "rgba(0,0,0,.32)",
    color: "white",
    textAlign: "left",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    fontSize: "clamp(17px, 2.5vw, 22px)",
    lineHeight: 1.5,
    cursor: "pointer",
  },
  mediaButton: {
    position: "relative",
    width: "100%",
    display: "block",
    padding: 0,
    borderRadius: 15,
    border: "1px solid rgba(255,255,255,.1)",
    background: "#000",
    overflow: "hidden",
    cursor: "pointer",
  },
  photo: { width: "100%", maxHeight: "70vh", display: "block", objectFit: "contain", background: "#000" },
  expandPill: {
    position: "absolute",
    right: 10,
    bottom: 10,
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(0,0,0,.76)",
    color: "white",
    fontSize: 12,
    fontWeight: 900,
  },
  videoPreview: {
    position: "relative",
    borderRadius: 15,
    border: "1px solid rgba(255,255,255,.1)",
    background: "#000",
    overflow: "hidden",
  },
  videoExpandButton: {
    position: "absolute",
    right: 10,
    bottom: 10,
    minHeight: 36,
    padding: "0 11px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.18)",
    background: "rgba(0,0,0,.78)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  },
  waitingMedia: {
    minHeight: 160,
    borderRadius: 15,
    border: "1px solid rgba(255,255,255,.1)",
    background: "rgba(0,0,0,.35)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  },
  actionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 8,
  },
  actionButton: { ...baseButton, minWidth: 0, fontSize: 12, padding: "0 7px" },
  actionLiked: {
    ...baseButton,
    minWidth: 0,
    fontSize: 12,
    padding: "0 7px",
    borderColor: "rgba(255,91,115,.56)",
    color: "#ff9cad",
    background: "rgba(180,18,49,.25)",
  },
  deleteButton: {
    ...baseButton,
    minWidth: 0,
    fontSize: 12,
    padding: "0 7px",
    borderColor: "rgba(255,90,90,.28)",
    background: "rgba(110,0,0,.2)",
  },
  message: {
    marginTop: 12,
    padding: 10,
    borderRadius: 12,
    background: "rgba(255,255,255,.06)",
    border: "1px solid rgba(255,255,255,.1)",
  },
  empty: {
    marginTop: 12,
    padding: 18,
    borderRadius: 14,
    border: "1px dashed rgba(255,255,255,.12)",
    color: "rgba(255,255,255,.65)",
  },
  expandedText: {
    padding: 24,
    color: "white",
    fontSize: "clamp(20px, 4vw, 32px)",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  commentsList: { display: "grid", gap: 10, marginTop: 12 },
  commentCard: {
    padding: 12,
    borderRadius: 13,
    border: "1px solid rgba(255,255,255,.1)",
    background: "rgba(255,255,255,.04)",
  },
  uploadModalBody: { padding: 16, display: "grid", gap: 14, maxHeight: "72vh", overflowY: "auto" },
};